import { createIntersectionObserver } from "@solid-primitives/intersection-observer";
import fuzzysort from "fuzzysort";
import type { Room } from "sdk";
import { createUpload, getTimestampFromUUID } from "sdk";
import {
	createEffect,
	createResource,
	createSignal,
	For,
	Show,
	type VoidProps,
} from "solid-js";
import { useApi, useChannels, useUsers } from "@/api";
import { Dropdown } from "@/atoms/Dropdown.tsx";
import { Time } from "@/atoms/Time.tsx";
import { Avatar } from "@/components/shared/User";
import { useModals } from "@/contexts/modal";
import { useConfig } from "@/lib/config";

// TODO(#750): group webhooks by channel

export function Webhooks(props: VoidProps<{ room: Room }>) {
	const api2 = useApi();
	const users2 = useUsers();
	const config = useConfig();
	const channels2 = useChannels();
	const [, modalCtl] = useModals();

	const [webhooks, { refetch }] = createResource(async () => {
		const { data } = await api2.client.http.GET(
			"/api/v1/room/{room_id}/webhook",
			{ params: { path: { room_id: props.room.id } } },
		);
		return data;
	});

	const removeWebhook = (webhook_id: string) => () => {
		modalCtl.confirm(
			"Are you sure you want to delete this webhook?",
			(conf) => {
				if (!conf) return;
				api2.client.http
					.DELETE("/api/v1/webhook/{webhook_id}", {
						params: { path: { webhook_id } },
					})
					.then(() => {
						refetch();
					});
			},
		);
	};

	const fetchMore = () => {
		// TODO: make this work
	};

	const [bottom, setBottom] = createSignal<Element | undefined>();

	createIntersectionObserver(
		() => (bottom() ? [bottom()!] : []),
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) fetchMore();
			}
		},
	);

	const [search, setSearch] = createSignal("");

	const filteredWebhooks = () => {
		const query = search();
		if (!query) return webhooks()?.items ?? [];
		const items = webhooks()?.items ?? [];
		const results = fuzzysort.go(query, items, {
			key: "name",
			threshold: -10000,
		});
		return results.map((r) => r.obj);
	};

	const create = () => {
		modalCtl.prompt("New webhook name?", async (name) => {
			if (!name) return;
			await api2.client.http.POST("/api/v1/room/{room_id}/webhook" as any, {
				params: { path: { room_id: props.room.id } },
				body: {
					name,
				},
			});
			refetch();
		});
	};

	return (
		<div class="room-settings-integrations">
			<h2>webhooks</h2>
			<header class="applications-header">
				<input
					type="search"
					placeholder="search"
					aria-label="search"
					onInput={(e) => setSearch(e.target.value)}
				/>
				<button type="button" class="primary big" onClick={create}>
					create
				</button>
			</header>
			<Show when={webhooks()}>
				<ul>
					<For each={filteredWebhooks()}>
						{(i) => {
							const creator = users2.use(() => i.creator_id as string);
							const [name, setName] = createSignal(i.name ?? "");
							const [avatar, setAvatar] = createSignal<string | null>(
								i.avatar ?? null,
							);

							const webhookUser = () => ({
								id: i.id,
								name: i.name,
								avatar: avatar(),
								banner: null,
								description: null,
								bot: true,
								system: false,
								version_id: "",
								flags: 0,
								presence: { status: "Offline" as const, activities: [] },
								preferences: null,
							});

							const channels = () =>
								[...channels2.cache.values()].filter(
									(c) => c.room_id === i.room_id,
								);

							createEffect(() => {
								setName(i.name);
							});

							const updateWebhook = async (channel_id: string | null) => {
								console.log(channel_id);
								if (!channel_id) return;
								return;
								await api2.client.http.PATCH("/api/v1/webhook/{webhook_id}", {
									params: { path: { webhook_id: i.id } },
									body: {
										name: name(),
									},
								});
							};

							const copyWebhookUrl = () => {
								const webhookUrl = `${config.api_url}/api/v1/webhook/${i.id}/${i.token}`;
								navigator.clipboard.writeText(webhookUrl);
							};

							const setAvatarFile = async (f: File) => {
								await createUpload({
									client: api2.client,
									file: f,
									onComplete(media) {
										setAvatar(media.id);
										api2.client.http.PATCH("/api/v1/webhook/{webhook_id}", {
											params: { path: { webhook_id: i.id } },
											body: {
												avatar: media.id,
											},
										});
									},
									onFail(_error) {},
									onPause() {},
									onResume() {},
									onProgress(_progress) {},
								});
							};

							const removeAvatar = async () => {
								setAvatar(null);
								await api2.client.http.PATCH("/api/v1/webhook/{webhook_id}", {
									params: { path: { webhook_id: i.id } },
									body: {
										avatar: null,
									},
								});
							};

							let avatarInputEl!: HTMLInputElement;

							const openAvatarPicker = () => {
								avatarInputEl?.click();
							};

							return (
								<li>
									<details>
										<summary>
											<div style="display: flex; align-items: top; gap: 8px;">
												<Avatar user={webhookUser()} />
												<div>
													<h3 class="name">{name()}</h3>
													<div>
														created on{" "}
														<Time date={getTimestampFromUUID(i.id)} /> by{" "}
														{creator()?.name}
													</div>
												</div>
											</div>
										</summary>
										<div class="info">
											<div style="display: flex; align-items: center; gap: 8px;">
												<div class="avatar-uploader" onClick={openAvatarPicker}>
													<div class="avatar-inner">
														<Avatar user={webhookUser()} />
														<div class="overlay">upload avatar</div>
													</div>
													<Show when={webhookUser().avatar}>
														<button
															type="button"
															class="remove"
															onClick={(e) => {
																e.stopPropagation();
																removeAvatar();
															}}
														>
															remove
														</button>
													</Show>
													<input
														style="display:none"
														ref={avatarInputEl}
														type="file"
														onInput={(e) => {
															const f = e.target.files?.[0];
															if (f) setAvatarFile(f);
														}}
													/>
												</div>
												<div>
													<div style="display: flex;flex: 1; gap: 8px">
														<div style="flex: 1;">
															<h3 class="dim">name</h3>
															<input
																type="text"
																value={name()}
																onInput={(e) => setName(e.target.value)}
																onBlur={updateWebhook as any}
															/>
														</div>
														<Show when={false}>
															{/* FIXME: changing the channel of webhook */}
															{/* for some reaosn, onSelect doesnt call the fn with the value, only undefined */}
															<div style="flex: 1;">
																<h3 class="dim">channel</h3>
																<Dropdown
																	selected={i.channel_id}
																	onSelect={(i) => console.log(i)}
																	options={
																		(channels().map((ch) => ({
																			label:
																				ch.name ||
																				`#${ch.id.substring(0, 8)}...`,
																			value: ch.id,
																		})) as any) || []
																	}
																/>
															</div>
														</Show>
													</div>
													<div style="margin-top: 8px; display: flex; gap: 8px">
														<button
															type="button"
															class="button"
															onClick={copyWebhookUrl}
														>
															copy url
														</button>
														<button
															type="button"
															onClick={removeWebhook(i.id)}
															class="destructive"
														>
															delete
														</button>
													</div>
												</div>
											</div>
										</div>
									</details>
								</li>
							);
						}}
					</For>
				</ul>
				<div ref={setBottom}></div>
			</Show>
		</div>
	);
}
